import * as React from 'react'

import {
  Box,
  Icon,
  Popover,
  Table,
  Tag,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { Features } from 'generated/featureDefinitions'
import {
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
  UpdateAttendanceShiftMutationVariables,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { OverflowContextProvider } from 'modules/core/lib/overflowContext'

import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { useAttendanceBreaks } from 'modules/attendance/lib/attendanceBreaksContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { shiftLocationTagAttributes } from 'modules/attendance/lib/shiftLocationTag'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import { getProjectSubprojectName } from 'modules/project_management/lib/formatText'

import { shouldHideLocationColumn } from '../../../ShiftRowAccordion/utils'
import ShiftCategoryTag from '../../ShiftCategoryTag'
import { LocationTag } from '../LocationTag'
import MapSidePanel from '../ShiftLocationMap/MapSidePanel'

interface ObservationsProps {
  toggle: (val: boolean) => () => void
  open: boolean
  observations: string
  disabled?: boolean
}

const Observations = ({ toggle, open, observations, disabled }: ObservationsProps) => {
  if (!!disabled) return <Icon.Medium icon="comment" color="grey400" />

  return (
    <Popover.ItemList
      attachment="top center"
      targetAttachment="bottom center"
      toggle={toggle}
      open={open}
      title={i18n.t('clock_in.history.observation_title')}
    >
      <Box cursor="pointer" padding="s4">
        <Icon.Medium icon="comment" color="viridian1000" />
      </Box>
      <Box alignItems="center" background="grey800">
        <Text.Normal size="200" color="grey000White">
          {observations}
        </Text.Normal>
      </Box>
    </Popover.ItemList>
  )
}

export const ObservationsMemo = React.memo(Observations)

interface Props {
  shift: TimesheetPageShiftFragment
  projectName?: string
  subprojectName: string
  form: FormikProps<UpdateAttendanceShiftMutationVariables>
  checkSubmit: () => Promise<void>
  attendanceSetting?: TimesheetPageEmployeesSettingFragment
  dayBreaks: AttendanceBreakConfigurations[]
  selectedBreak?: AttendanceBreakConfigurations
  shiftLocations?: AttendanceShiftLocationNodes
  hasProjects?: boolean
}

const ShiftDetailColumns = ({
  shift,
  projectName,
  subprojectName,
  selectedBreak,
  checkSubmit,
  form,
  dayBreaks,
  shiftLocations,
  attendanceSetting,
  hasProjects = false,
}: Props) => {
  const ref = React.useRef(null)
  const { workable, locationType, timeSettingsBreakConfiguration, automaticClockIn } = shift
  const hasPaidBreaksFeature = hasFeature('time_tracking_paid_breaks')

  const {
    enableBreaks = false,
    editTimesheetAuthorization = false,
    enableClockInLocation = false,
    geofencing = false,
    geolocation = false,
  } = attendanceSetting ?? {}

  const { featureAvailability: hasGeofencingFeature } = useAttendanceFeatures(
    Features.time_tracking_geofencing_alerts
  )

  const { allBreaks } = useAttendanceBreaks()

  const locationIssue = shiftLocations?.some((location) => location.workplaceMissingInfo)
  const outOfRadiusIssue = !!shiftLocations?.some((location) => location.outOfRadius)
  const hasShiftLocations = !!shiftLocations?.length
  const [open, toggle] = useToggle(false)

  const { featureAvailability: hasSubprojectsFeature } = useAttendanceFeatures(
    Features.projects_with_subprojects
  )
  const openShift = AttendanceShiftUtils.isOpenShift(shift)
  const { malibu } = Tag.Types
  const workplaceName = shift.workplace?.name

  const hasLocationIssueAndGeofencing = (locationIssue || outOfRadiusIssue) && geofencing

  const {
    textLabel: shiftLocationLabel,
    tagColor: shiftLocationColor,
    icon: shiftLocationIcon,
  } = shiftLocationTagAttributes({
    hasLocationIssueAndGeofencing,
    outOfRadiusIssue,
    locationType,
    workplaceName,
    hasShiftLocations,
    hasGeolocation: geolocation,
    hasGeofencing: geofencing,
    hasLocation: enableClockInLocation,
  })

  const shiftLocationLabelExists = !!shiftLocationLabel.length
  const showTag =
    shiftLocationLabelExists &&
    (enableClockInLocation || (hasGeofencingFeature && (hasLocationIssueAndGeofencing || workable)))

  const [showMapPanel, setShowMapPanel] = React.useState<boolean>(false)

  const showBreaksList =
    hasPaidBreaksFeature &&
    enableBreaks &&
    !workable &&
    !openShift &&
    !editTimesheetAuthorization &&
    dayBreaks.length > 1

  const hideLocationColumn = shouldHideLocationColumn(attendanceSetting)

  return (
    <>
      <Table.Cell verticalAlign="middle" wide noBorder>
        <ShiftCategoryTag
          form={form}
          workable={!!workable}
          allBreaks={allBreaks}
          dayBreaks={dayBreaks}
          checkSubmit={checkSubmit}
          selectedBreak={selectedBreak}
          showBreaksList={showBreaksList}
          timeSettingsBreakConfigurationId={timeSettingsBreakConfiguration?.id}
          automaticClockIn={!!automaticClockIn}
        />
      </Table.Cell>
      {!hideLocationColumn && (
        <Table.Cell verticalAlign="middle" wide noBorder>
          {showTag ? (
            <LocationTag
              open={open}
              toggle={toggle}
              icon={shiftLocationIcon}
              locationIssue={locationIssue}
              hasGeofencing={geofencing}
              tagColor={shiftLocationColor}
              textLabel={shiftLocationLabel}
              shiftLocations={shiftLocations}
              setShowMapPanel={setShowMapPanel}
              outOfRadiusIssue={outOfRadiusIssue}
              attendanceSetting={attendanceSetting}
            />
          ) : (
            <Text.Normal size="200" color="grey600">
              —
            </Text.Normal>
          )}
        </Table.Cell>
      )}
      {hasProjects && (
        <Table.Cell verticalAlign="middle" wide noBorder>
          {!!projectName ? (
            <OverflowContextProvider childrenRef={ref} value={projectName}>
              <Box paddingY="s12" width="fit-content">
                <Tag type={malibu}>
                  {getProjectSubprojectName(projectName, subprojectName, hasSubprojectsFeature)}
                </Tag>
              </Box>
            </OverflowContextProvider>
          ) : (
            <Text.Normal size="200" color="grey600">
              —
            </Text.Normal>
          )}
        </Table.Cell>
      )}
      {!!shiftLocations && (
        <MapSidePanel
          show={showMapPanel}
          onClose={() => setShowMapPanel(false)}
          shiftLocations={shiftLocations}
          hasGeofencingEnabled={hasGeofencingFeature}
        />
      )}
    </>
  )
}

export default observer(ShiftDetailColumns)
